/* oddit-section-ugc — infinite, draggable carousel.

   Position is ONE unbounded float `x` (px, content-space), rendered every frame as a
   transform on the inner strip. The track is not a scroll container: there is no
   scrollLeft, no scroll-snap, no scrollend, and therefore no teleport and nothing to race.

   The loop is a modulo, not an event. The strip repeats every `setW` px, so content at
   `x` and at `x + setW` is identical — wrapping `x` in the same frame that writes the
   transform is an exact relabeling of which physical card sits in which slot.

   DOM: [clones set 0][ORIGINALS set 1][clones set 2..K-1] */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-ugc';
  var CARD_SELECTOR = '.oddit-section-ugc__card';
  var INNER_SELECTOR = '.oddit-section-ugc__track-inner';
  var DESKTOP_MQ = '(min-width: 990px)';

  var DRAG_THRESHOLD = 5;    // px before a press becomes a drag
  var SPRING = 14;           // rad/s — critically-damped settle
  var FLING_MS = 120;        // how far ahead a release velocity is projected
  var MIN_FLING = 0.45;      // px/ms — below this a release is a STOP, not a throw
  var MAX_FLING_CARDS = 2;   // a hard throw must not spin the strip like a slot machine
  var SAMPLE_MS = 100;       // velocity is averaged over this window, not one sample

  var instances = new Map();

  function slice(nodes) {
    return Array.prototype.slice.call(nodes);
  }

  function initSection(section) {
    destroySection(section);

    var track = section.querySelector('[data-oddit-ugc-track]');
    var inner = section.querySelector(INNER_SELECTOR);
    if (!track || !inner) return;

    var reals = slice(inner.querySelectorAll(CARD_SELECTOR));
    var N = reals.length;
    if (N < 2) return;

    var dots = slice(section.querySelectorAll('[data-oddit-ugc-dot]'));
    var prevBtn = section.querySelector('[data-oddit-ugc-prev]');
    var nextBtn = section.querySelector('[data-oddit-ugc-next]');
    var reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var desktopMq = window.matchMedia(DESKTOP_MQ);
    var ac = new AbortController();
    var opts = { signal: ac.signal };

    var x = 0;             // content-space position; increases as the strip moves left
    var vel = 0;           // px/s, seeds the settle spring
    var target = null;     // null => free (dragging / wheeling)
    var pitch = 0, setW = 0, cardW = 0, gap = 0, origin = 0, K = 0;
    var raf = null, lastT = 0, lastW = 0;
    var activeDot = -1;
    var samples = [];
    var drag = null;
    var justDragged = false;
    var wheelTimer = null, resizeTimer = null;

    /* ---------- clones ---------- */

    function sanitizeClone(node) {
      // aria-hidden + tabindex=-1 (below) removes clones from the a11y tree and
      // focus order. Deliberately NOT `inert`: inert nodes are skipped by
      // hit-testing, which killed the hover lift/zoom on every cloned card.
      node.setAttribute('aria-hidden', 'true');
      node.setAttribute('data-oddit-clone', '');
      slice(node.querySelectorAll('a, button, input, select, textarea, [tabindex]')).forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      slice(node.querySelectorAll('[data-setting-id]')).forEach(function (el) {
        el.removeAttribute('data-setting-id');
      });
      // `url(#id)` resolves to the FIRST match in the document, so a clone's clip-path
      // would silently reference the original card's def.
      slice(node.querySelectorAll('[id]')).forEach(function (el) { el.removeAttribute('id'); });
      slice(node.querySelectorAll('[clip-path]')).forEach(function (el) { el.removeAttribute('clip-path'); });
      // The IntersectionObserver in oddit-interactions.js never sees nodes added after
      // init, so an un-stripped reveal clone would stay at opacity: 0 forever.
      if (node.className && /\boddit-reveal/.test(node.className)) {
        node.className = node.className.replace(/\boddit-reveal\S*/g, '').trim();
        node.style.opacity = '';
        node.style.transform = '';
      }
      slice(node.querySelectorAll('[class*="oddit-reveal"]')).forEach(function (el) {
        el.className = el.className.replace(/\boddit-reveal\S*/g, '').trim();
        el.style.opacity = '';
        el.style.transform = '';
      });
      return node;
    }

    function buildStrip(k) {
      // A clone that is mid-playback would keep its audio going once detached.
      resetVideos();
      slice(inner.querySelectorAll('[data-oddit-clone]')).forEach(function (el) { el.remove(); });

      var lead = document.createDocumentFragment();
      reals.forEach(function (card) { lead.appendChild(sanitizeClone(card.cloneNode(true))); });
      inner.insertBefore(lead, reals[0]);

      var tail = document.createDocumentFragment();
      for (var s = 2; s < k; s++) {
        reals.forEach(function (card) { tail.appendChild(sanitizeClone(card.cloneNode(true))); });
      }
      inner.appendChild(tail);
      K = k;
    }

    /* ---------- video cards ---------- */

    // A video card shows its poster until pointed at: hovering plays a muted
    // PREVIEW_MS preview, clicking plays it from the top with sound. Delegated on
    // the track so cloned cards behave exactly like the originals they copy.

    var PREVIEW_MS = 3000;
    var hoverMq = window.matchMedia('(hover: hover)');
    var previewCard = null;
    var previewTimer = null;
    var soundCard = null;

    function videoIn(card) {
      return card ? card.querySelector('.oddit-section-ugc__video') : null;
    }

    function rewind(video) {
      try { video.currentTime = 0; } catch (err) {}
    }

    function stopPreview() {
      clearTimeout(previewTimer);
      previewTimer = null;
      var card = previewCard;
      previewCard = null;
      if (!card || card === soundCard) return;
      var video = videoIn(card);
      if (video) {
        video.pause();
        rewind(video);
      }
      card.setAttribute('data-ugc-state', 'idle');
    }

    function stopSound() {
      var card = soundCard;
      soundCard = null;
      if (!card) return;
      var video = videoIn(card);
      if (video) {
        video.pause();
        video.muted = true;
        rewind(video);
      }
      card.setAttribute('data-ugc-state', 'idle');
    }

    function resetVideos() {
      stopPreview();
      stopSound();
    }

    function startPreview(card) {
      // Touch taps synthesise mouseover in some browsers; there is no hover to
      // preview on those, and the tap already means "play with sound".
      if (!hoverMq.matches || reducedMq.matches) return;
      if (soundCard || card === previewCard) return;
      var video = videoIn(card);
      if (!video) return;
      stopPreview();
      previewCard = card;
      video.muted = true;
      rewind(video);
      card.setAttribute('data-ugc-state', 'preview');
      var played = video.play();
      if (played && played.catch) played.catch(function () {});
      previewTimer = setTimeout(stopPreview, PREVIEW_MS);
    }

    function playWithSound(card) {
      if (card === soundCard) {
        stopSound();
        return;
      }
      stopPreview();
      stopSound();
      var video = videoIn(card);
      if (!video) return;
      soundCard = card;
      video.muted = false;
      rewind(video);
      card.setAttribute('data-ugc-state', 'playing');
      var played = video.play();
      if (played && played.catch) played.catch(function () { stopSound(); });
    }

    function cardFrom(e) {
      return e.target && e.target.closest ? e.target.closest('[data-ugc-video]') : null;
    }

    track.addEventListener('mouseover', function (e) {
      var card = cardFrom(e);
      if (!card) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      startPreview(card);
    }, opts);

    track.addEventListener('mouseout', function (e) {
      var card = cardFrom(e);
      if (!card || card !== previewCard) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      stopPreview();
    }, opts);

    track.addEventListener('click', function (e) {
      var card = cardFrom(e);
      if (!card) return;
      e.preventDefault();
      playWithSound(card);
    }, opts);

    // `ended` does not bubble, so it is caught on the way down.
    track.addEventListener('ended', function (e) {
      var card = cardFrom(e);
      if (!card) return;
      if (card === soundCard) stopSound();
      else if (card === previewCard) stopPreview();
    }, { capture: true, signal: ac.signal });

    /* ---------- measure ---------- */

    // The period is measured from the DOM, never reconstructed as N * (cardW + gap):
    // offsetLeft/offsetWidth are integer-rounded, so under browser zoom or a fractional
    // DPR the reconstructed period drifts 1-3px from the real one and the strip steps
    // sideways on every wrap.
    function measure() {
      var cards = inner.querySelectorAll(CARD_SELECTOR);
      if (cards.length < N + 1) return false;

      var first = cards[0].getBoundingClientRect();
      var next = cards[N].getBoundingClientRect();
      setW = next.left - first.left;
      if (!(setW > 0)) return false;

      pitch = setW / N;
      cardW = first.width;
      gap = pitch - cardW;

      var W = track.clientWidth;
      if (desktopMq.matches) {
        // Centre the GAP on the viewport. At 1440 this puts card 0's left edge at exactly
        // -100px, matching the Figma baseline, and it stays symmetric at every width.
        origin = (W + gap) / 2 - pitch * Math.ceil((W + gap) / (2 * pitch));
      } else {
        origin = parseFloat(getComputedStyle(section).getPropertyValue('--oddit-ugc-gutter')) || 0;
      }
      return true;
    }

    // Visible span in content coords is [wrapped + setW - origin, ... + trackW], so the
    // strip must cover 2*setW + trackW - origin. The `- origin` matters: a negative origin
    // (desktop bleed) pushes the window right.
    function neededK() {
      return Math.max(3, Math.ceil((track.clientWidth - origin) / setW) + 2);
    }

    /* ---------- render ---------- */

    function render() {
      var wrapped = ((x % setW) + setW) % setW;   // always in [0, setW)
      inner.style.transform = 'translate3d(' + (origin - wrapped - setW) + 'px, 0, 0)';
    }

    function indexAt(pos) {
      return pitch ? Math.round(pos / pitch) : 0;
    }

    function updateDots() {
      if (!dots.length) return;
      var i = ((indexAt(x) % N) + N) % N;
      if (i === activeDot) return;
      activeDot = i;
      dots.forEach(function (dot, k) {
        var on = k === i;
        dot.classList.toggle('oddit-section-ugc__dot--active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    /* ---------- animation ---------- */

    function tick(now) {
      raf = null;
      var dt = Math.min((now - lastT) / 1000, 0.05);   // clamp: a backgrounded tab returns one huge frame
      lastT = now;

      if (target !== null) {
        if (reducedMq.matches) {
          x = target;
          vel = 0;
        } else {
          // Exact critically-damped spring: d(t) = (d0 + B*t)*e^(-w*t), with B = v + w*d0.
          // It is B, not d(t), that appears in the velocity term — using d(t) there adds a
          // spurious -w*d0 each frame, so the spring arrives hot, overshoots and rings.
          var d0 = x - target;
          var b = vel + SPRING * d0;
          var e = Math.exp(-SPRING * dt);
          x = target + (d0 + b * dt) * e;
          vel = (vel - SPRING * b * dt) * e;
        }
        if (Math.abs(x - target) < 0.1 && Math.abs(vel) < 0.1) {
          x = target;
          vel = 0;
          target = null;
        }
      }

      render();
      updateDots();

      if (drag || target !== null) raf = requestAnimationFrame(tick);
      else stopLoop();
    }

    function startLoop() {
      if (raf) return;
      lastT = performance.now();
      inner.style.willChange = 'transform';
      raf = requestAnimationFrame(tick);
    }

    function stopLoop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      inner.style.willChange = '';   // a K-set strip is a very wide composited layer; don't hold it
    }

    function settle(index) {
      target = index * pitch;
      startLoop();
    }

    function snapToNearest() {
      settle(indexAt(x));
    }

    // Shortest way round the ring — x is unbounded, so this wraps for free.
    function goToLogical(i) {
      var cur = indexAt(x);
      var delta = i - (((cur % N) + N) % N);
      if (delta > N / 2) delta -= N;
      if (delta < -N / 2) delta += N;
      settle(cur + delta);
    }

    /* ---------- velocity ---------- */

    function releaseVelocity() {
      // Stamp the release itself. pointermove only fires while the pointer is MOVING, so
      // without this a drag that coasts to a stop would still throw with a stale speed.
      samples.push({ t: performance.now(), x: x });

      var n = samples.length;
      var last = samples[n - 1];
      var first = samples[0];
      for (var i = n - 1; i >= 0; i--) {
        if (last.t - samples[i].t > SAMPLE_MS) break;
        first = samples[i];
      }
      var dt = last.t - first.t;
      if (dt < 8) return 0;               // one noisy sample is not a velocity
      var v = (last.x - first.x) / dt;    // px/ms, averaged over the window
      return Math.abs(v) < MIN_FLING ? 0 : v;
    }

    /* ---------- drag (mouse + touch, one path) ---------- */

    // No axis lock here on purpose: `touch-action: pan-y` lets the browser make that call
    // with the platform's own heuristics, and it tells us by firing pointercancel.
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.pointerType === 'mouse') e.preventDefault();   // kill text-selection / native image drag

      target = null;
      vel = 0;
      justDragged = false;
      samples.length = 0;
      samples.push({ t: performance.now(), x: x });
      drag = { id: e.pointerId, startX: e.clientX, startPos: x, moved: false };
      startLoop();
    }, opts);

    track.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.startX;

      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(drag.id); } catch (err) {}
      }

      // Absolute from the drag origin, never an accumulated += dx: no drift over a long
      // drag. There is no clamp — the strip is infinite, so a drag never hits a wall.
      x = drag.startPos - dx;
      samples.push({ t: performance.now(), x: x });
      if (samples.length > 12) samples.shift();
    }, opts);

    function endDrag(e, cancelled) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var moved = drag.moved;
      try { track.releasePointerCapture(drag.id); } catch (err) {}
      drag = null;
      track.classList.remove('is-dragging');

      if (!moved) {
        if (target === null) stopLoop();
        return;
      }
      justDragged = true;

      // A cancel means the browser took the gesture (vertical page scroll): settle where
      // we are rather than freezing mid-card.
      var v = cancelled ? 0 : releaseVelocity();   // px/ms
      vel = v * 1000;                              // px/s, seeds the spring

      var cur = indexAt(x);
      var projected = indexAt(x + v * FLING_MS);
      settle(Math.max(cur - MAX_FLING_CARDS, Math.min(cur + MAX_FLING_CARDS, projected)));
    }

    track.addEventListener('pointerup', function (e) { endDrag(e, false); }, opts);
    track.addEventListener('pointercancel', function (e) { endDrag(e, true); }, opts);

    // Swallow the click that ends a drag so a card link doesn't fire.
    track.addEventListener('click', function (e) {
      if (!justDragged) return;
      e.preventDefault();
      e.stopPropagation();
      justDragged = false;
    }, { capture: true, signal: ac.signal });

    /* ---------- wheel / keyboard / focus / controls ---------- */

    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   // vertical intent: let the page scroll
      e.preventDefault();                                     // horizontal: ours (and not browser back-nav)
      target = null;
      vel = 0;
      x += e.deltaX;
      startLoop();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(snapToNearest, 120);
    }, { passive: false, signal: ac.signal });

    track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      settle(indexAt(x) + (e.key === 'ArrowRight' ? 1 : -1));
    }, opts);

    // Keyboard focus must bring its card into view — the browser can't do it for us
    // (overflow-x: clip is deliberately not a scroll container). Clone focusables all
    // carry tabindex="-1", and the guard below drops any that still land here.
    inner.addEventListener('focusin', function (e) {
      var card = e.target.closest ? e.target.closest(CARD_SELECTOR) : null;
      if (!card || card.hasAttribute('data-oddit-clone')) return;
      var i = reals.indexOf(card);
      if (i >= 0) goToLogical(i);
    }, opts);

    if (prevBtn) {
      prevBtn.addEventListener('click', function () { settle(indexAt(x) - 1); }, opts);
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { settle(indexAt(x) + 1); }, opts);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goToLogical(i); }, opts);
    });

    /* ---------- layout ---------- */

    function relayout() {
      var i = indexAt(x);              // preserve the logical card across a remeasure
      if (!measure()) return;
      var k = neededK();
      if (k !== K) {
        buildStrip(k);
        if (!measure()) return;
      }
      x = i * pitch;
      vel = 0;
      target = null;
      lastW = track.clientWidth;
      render();
      updateDots();
    }

    var ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          // Height-only changes (the mobile URL bar) fire this constantly.
          if (track.clientWidth === lastW) return;
          relayout();
        }, 150);
      });
      ro.observe(track);
    } else {
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(relayout, 150);
      }, opts);
    }

    buildStrip(3);
    if (!measure()) return;
    var k0 = neededK();
    if (k0 !== 3) {
      buildStrip(k0);
      if (!measure()) return;
    }
    lastW = track.clientWidth;
    render();
    updateDots();

    // Card widths shift as fonts land.
    requestAnimationFrame(relayout);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(relayout).catch(function () {});
    }
    window.addEventListener('load', relayout, opts);

    instances.set(section, {
      destroy: function () {
        ac.abort();
        resetVideos();
        if (raf) cancelAnimationFrame(raf);
        clearTimeout(wheelTimer);
        clearTimeout(resizeTimer);
        if (ro) ro.disconnect();
        slice(inner.querySelectorAll('[data-oddit-clone]')).forEach(function (el) { el.remove(); });
        inner.style.transform = '';
        inner.style.willChange = '';
        track.classList.remove('is-dragging');
      }
    });
  }

  function destroySection(section) {
    var inst = instances.get(section);
    if (!inst) return;
    inst.destroy();
    instances.delete(section);
  }

  function init() {
    slice(document.querySelectorAll(SECTION_SELECTOR)).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
